/**
 * Agents Overview Page.
 *
 * Shows agent status card (admin-only) with drill-in actions,
 * and prompt versions table below.
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Settings, Maximize, AppWindow, CheckCircle2, XCircle, Loader2, Plus, FlaskConical, Eye, Trash2 } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import salesAgentService from '@/lib/api/salesAgent';
import customDomainsService from '@/lib/api/customDomains';
import { getEmbeddedDemoUrl, getLiveDemoUrl, getStagingDemoUrl } from '@/lib/demoUrls';
import promptVersionsService from '@/lib/api/promptVersions';
import type { PromptVersion, TransitionRequest, VersionsListResponse } from '@/lib/api/promptVersions';

function statusBadge(status: string) {
  switch (status) {
    case 'live':
      return <Badge className="bg-green-600/15 text-green-700 dark:text-green-400 border-green-600/20">Live</Badge>;
    case 'staging':
      return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20">Staging</Badge>;
    default:
      return <Badge variant="secondary">Draft</Badge>;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: undefined }) +
    ', ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const AgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization, isAdmin } = useOrganization();
  const orgId = currentOrganization?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: salesAgent, isLoading: isAgentLoading } = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await salesAgentService.getSalesAgent(orgId);
      if (response.success && response.data) return response.data;
      return null;
    },
    enabled: !!orgId && isAdmin,
    retry: false,
  });

  const { data: customDomainState } = useQuery({
    queryKey: ['custom-domain', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const response = await customDomainsService.getCustomDomain(orgId);
      if (response.success && response.data) return response.data;
      return null;
    },
    enabled: !!orgId,
    retry: false,
  });

  const liveDemoUrl = getLiveDemoUrl(salesAgent?.demo_slug, customDomainState?.custom_domain);
  const embeddedDemoUrl = getEmbeddedDemoUrl(salesAgent?.demo_slug);
  const stagingDemoUrl = getStagingDemoUrl(salesAgent?.staging_slug);

  // Fetch versions list
  const { data: versionsData, isLoading: isVersionsLoading } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await promptVersionsService.listVersions(orgId);
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Failed to load versions');
    },
    enabled: !!orgId,
  });

  // Transition mutation
  const transitionMutation = useMutation({
    mutationFn: async (body: TransitionRequest) => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await promptVersionsService.transitionVersion(orgId, body);
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Transition failed');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prompt-versions', orgId] });
      queryClient.invalidateQueries({ queryKey: ['sales-agent', orgId] });
      toast({ title: 'Success', description: data.message });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const handleTransition = (version: PromptVersion) => {
    const action: TransitionRequest['action'] =
      version.status === 'draft' ? 'deploy_to_staging' : 'publish_to_live';
    transitionMutation.mutate({ version: version.version, action });
  };

  const handlePreview = (version: PromptVersion) => {
    if (version.status === 'live' && liveDemoUrl) {
      window.open(liveDemoUrl, '_blank');
    } else if (version.status === 'staging' && stagingDemoUrl) {
      window.open(stagingDemoUrl, '_blank');
    }
  };

  // ── Delete version ──
  const [versionToDelete, setVersionToDelete] = useState<number | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (version: number) => {
      if (!orgId) throw new Error('Organization ID required');
      const res = await promptVersionsService.deleteVersion(orgId, version);
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Failed to delete version');
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['prompt-versions', orgId], (old: VersionsListResponse | undefined) =>
        old ? { ...old, versions: old.versions.filter((v) => v.version !== data.version) } : old
      );
      queryClient.invalidateQueries({ queryKey: ['prompt-versions', orgId] });
      toast({ title: 'Success', description: data.message });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const versions = versionsData?.versions ?? [];

  // ── Version click → compare against live ──
  const liveVersion = versions.find((v) => v.status === 'live');

  const handleVersionClick = (v: PromptVersion) => {
    if (!liveVersion || liveVersion.version === v.version) {
      navigate(`/agents/configure?version=${v.version}`);
    } else {
      const base = Math.min(liveVersion.version, v.version);
      const compare = Math.max(liveVersion.version, v.version);
      navigate(`/agents/configure?base=${base}&compare=${compare}&selected=${v.version}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Agents | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader title="Versions" />

        {/* Content */}
        <div className="flex-1 px-4 sm:px-6 md:px-8 pb-8 space-y-8">
          {/* Agent Status Card (admin only) */}
          {isAdmin && (
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-3">Agent</h2>
              {isAgentLoading ? (
                <Card>
                  <CardContent className="p-6 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </CardContent>
                </Card>
              ) : salesAgent ? (
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-sm truncate">
                            {salesAgent.agent_name || salesAgent.name || 'Unnamed Agent'}
                          </h3>
                          <Badge variant={salesAgent.is_active ? 'default' : 'secondary'} className="shrink-0">
                            {salesAgent.is_active ? (
                              <><CheckCircle2 className="h-3 w-3 mr-1" />Active</>
                            ) : (
                              <><XCircle className="h-3 w-3 mr-1" />Inactive</>
                            )}
                          </Badge>
                        </div>
                        {salesAgent.company_name && (
                          <p className="text-xs text-muted-foreground mt-1">{salesAgent.company_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!liveDemoUrl}
                          onClick={() => liveDemoUrl && window.open(liveDemoUrl, '_blank')}
                        >
                          <AppWindow className="h-3.5 w-3.5 mr-1.5" />
                          Full Page
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!embeddedDemoUrl}
                          onClick={() => embeddedDemoUrl && window.open(embeddedDemoUrl, '_blank')}
                        >
                          <Maximize className="h-3.5 w-3.5 mr-1.5" />
                          Embedded
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-sm">No agent yet</h3>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate('/agents/create-version')}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Create Agent
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </section>
          )}

          {/* Versions Table */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-muted-foreground">Versions</h2>
              {isAdmin && (
                salesAgent ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/agents/configure')}
                    >
                      <Settings className="h-3.5 w-3.5 mr-1.5" />
                      Configure
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/training?tab=simulations')}
                    >
                      <FlaskConical className="h-3.5 w-3.5 mr-1.5" />
                      Evaluate
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate('/agents/create-version')}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      Coach
                    </Button>
                  </div>
                ) : null
              )}
            </div>
            {isVersionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-sm text-muted-foreground">
                  {salesAgent
                    ? 'No versions yet.'
                    : 'Create your agent to get started.'}
                </p>
              </div>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Published Date</TableHead>
                      <TableHead>Created by</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {versions.map((v, idx) => (
                      <TableRow key={v.version}>
                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell>
                          <button
                            className="font-medium text-left hover:underline cursor-pointer"
                            onClick={() => handleVersionClick(v)}
                          >
                            v{v.version}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(v.created_at)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{v.created_by ?? '—'}</TableCell>
                        <TableCell>{statusBadge(v.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled
                            >
                              Simulate
                            </Button>

                            {v.status === 'draft' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-blue-500 hover:bg-blue-600 text-white"
                                  onClick={() => handleTransition(v)}
                                  disabled={transitionMutation.isPending}
                                >
                                  Deploy to Staging
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  onClick={() => transitionMutation.mutate({ version: v.version, action: 'publish_to_live' })}
                                  disabled={transitionMutation.isPending}
                                >
                                  Publish Live
                                </Button>
                              </>
                            )}
                            {v.status === 'staging' && (
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 text-white"
                                onClick={() => handleTransition(v)}
                                disabled={transitionMutation.isPending}
                              >
                                Publish
                              </Button>
                            )}

                            {(v.status === 'live' || v.status === 'staging') && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handlePreview(v)}
                                disabled={
                                  (v.status === 'live' && !salesAgent?.demo_slug) ||
                                  (v.status === 'staging' && !salesAgent?.staging_slug)
                                }
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}

                            {v.status === 'draft' && (
                              deleteMutation.isPending && deleteMutation.variables === v.version ? (
                                <div className="h-8 w-8 flex items-center justify-center">
                                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={() => setVersionToDelete(v.version)}
                                  disabled={deleteMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </section>

          {/* Delete confirmation dialog */}
          <AlertDialog open={versionToDelete !== null} onOpenChange={(open) => { if (!open) setVersionToDelete(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete version v{versionToDelete}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this draft version. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    if (versionToDelete !== null) {
                      deleteMutation.mutate(versionToDelete);
                      setVersionToDelete(null);
                    }
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </div>
      </div>
    </>
  );
};

export default AgentsPage;
